<template>
    <div class="CTooltip inline-block" @mouseenter="activate" @focusin="activate">
        <div ref="trigger">
            <slot name="trigger">
                <CIcon :name="icon" size="small" />
            </slot>
        </div>

        <div
            ref="content"
            class="CTooltip--bubble-content pointer-events-none absolute z-50 inline-block w-auto max-w-80 origin-bottom rounded-lg px-4 py-3 text-sm font-normal shadow-lg"
            :class="{
                'hidden': !isActive,
                'bg-black text-center text-white': variant === 'dark',
                'bg-white dark:bg-surface-card text-black dark:text-white': variant === 'light',
                'max-w-[1100px] bg-white dark:bg-surface-card text-black dark:text-white': variant === 'light-large'
            }"
        >
            <slot name="default" />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { bottom, Instance } from '@popperjs/core'

import CIcon from '@/components/ui/CIcon.vue'
import { createPopper, top } from '@popperjs/core'
import { ref, nextTick, onBeforeUnmount } from 'vue'

const props = defineProps({
    placement: {
        type: String as PropType<typeof top | typeof bottom | 'top-end'>,
        default: top
    },
    icon: {
        type: String,
        default: 'info'
    },
    variant: {
        type: String,
        default: 'dark'
    }
})

const trigger = ref<HTMLElement | null>(null)
const content = ref<HTMLElement | null>(null)
const isActive = ref(false)
let tether: Instance | null = null

// Pages render dozens of tooltips and creating a Popper (with its scroll/resize listeners) for each
// on mount cost ~200 ms of main-thread time, so it's created on the first hover or focus instead.
async function activate() {
    if (isActive.value) return
    isActive.value = true
    // The bubble is display: none until now; let it render so Popper can measure it.
    await nextTick()
    if (!trigger.value || !content.value) return
    tether = createPopper(trigger.value, content.value, {
        placement: props.placement,
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 3]
                }
            }
        ]
    })
}

onBeforeUnmount(() => {
    tether?.destroy()
})
</script>

